import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, CheckCircle2, AlertTriangle, XCircle, Target, TrendingDown, ArrowRight, Info, Sparkles, ListChecks, ClipboardList, Filter as FilterIcon } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { Select } from '../../../../../components/ui/Select';
import { MODULE_CONFIG } from '../shared';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { AssessmentPoolHealthReport, AssessmentPoolSliceFinding, AssessmentPoolFeasibilityFinding } from '../hooks/useGovernanceData';
interface AssessmentPoolHealthTabProps {
  report: AssessmentPoolHealthReport;
  isRunning: boolean;
  onSelectQuestion: (questionId: string) => void;
}
type StatusKind = 'ok' | 'degraded' | 'shortfall' | 'zero' | 'unknown';
interface MatrixCell {
  group: GroupId;
  module: string;
  status: StatusKind;
  finding?: AssessmentPoolFeasibilityFinding;
}
/**
 * AssessmentPoolHealthTab — the full-page "Toetsen" tab.
 *
 * Answers one primary question prominently: "voor welke groep+module-combinatie
 * kunnen we een complete Cito-style toets samenstellen?" — and provides the
 * underlying capacity- and distribution-diagnostics as supporting evidence.
 *
 * Layout:
 *   1. Hero header with overall feasibility readiness
 *   2. KPI strip (4 cards)
 *   3. Feasibility matrix (group × module) — the centerpiece
 *   4. Two-column detail: capacity gaps | distribution issues
 *   5. Decision-hint footer
 */
export function AssessmentPoolHealthTab({
  report,
  isRunning,
  onSelectQuestion
}: AssessmentPoolHealthTabProps) {
  // ─── Derive modules present in the report ──────────────────────────────
  const allModules = useMemo(() => {
    const set = new Set<string>();
    Object.keys(report.byModule).forEach(m => set.add(m));
    report.feasibility.forEach(f => set.add(f.module));
    report.slices.forEach(s => set.add(s.module));
    return Array.from(set).sort();
  }, [report]);
  // ─── Build feasibility matrix (group × module) ─────────────────────────
  const feasibilityIndex = useMemo(() => {
    const map = new Map<string, AssessmentPoolFeasibilityFinding>();
    report.feasibility.forEach(f => map.set(`${f.group}::${f.module}`, f));
    return map;
  }, [report.feasibility]);
  const matrix: MatrixCell[][] = useMemo(() => {
    return GROUPS.map(group => allModules.map(module => {
      const finding = feasibilityIndex.get(`${group}::${module}`);
      let status: StatusKind = 'ok';
      if (!finding) {
        // No finding emitted means either OK or no module-data for that group.
        // We default to 'ok' but downgrade to 'unknown' when there's no
        // module data anywhere in the report (preserves honesty).
        status = report.byModule[module] !== undefined ? 'ok' : 'unknown';
      } else if (finding.reason === 'zero_questions') status = 'zero';else if (finding.reason === 'shortfall') status = 'shortfall';else if (finding.reason === 'degraded') status = 'degraded';
      return {
        group,
        module,
        status,
        finding
      };
    }));
  }, [allModules, feasibilityIndex, report.byModule]);
  // ─── Aggregate matrix counts ──────────────────────────────────────────
  const matrixCounts = useMemo(() => {
    let ok = 0;
    let degraded = 0;
    let shortfall = 0;
    let zero = 0;
    let unknown = 0;
    matrix.forEach(row => row.forEach(cell => {
      if (cell.status === 'ok') ok++;else if (cell.status === 'degraded') degraded++;else if (cell.status === 'shortfall') shortfall++;else if (cell.status === 'zero') zero++;else unknown++;
    }));
    const total = ok + degraded + shortfall + zero + unknown;
    const ready = ok;
    const readinessPct = total > 0 ? Math.round(ready / total * 100) : 0;
    return {
      ok,
      degraded,
      shortfall,
      zero,
      unknown,
      total,
      readinessPct
    };
  }, [matrix]);
  // ─── Filters ─────────────────────────────────────────────────────────
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const filteredSlices = useMemo(() => {
    return report.slices.filter(s => {
      if (moduleFilter !== 'all' && s.module !== moduleFilter) return false;
      return true;
    });
  }, [report.slices, moduleFilter]);
  const filteredFeasibility = useMemo(() => {
    return report.feasibility.filter(f => {
      if (moduleFilter !== 'all' && f.module !== moduleFilter) return false;
      if (groupFilter !== 'all' && String(f.group) !== groupFilter) return false;
      if (statusFilter !== 'all' && f.reason !== statusFilter) return false;
      return true;
    });
  }, [report.feasibility, moduleFilter, groupFilter, statusFilter]);
  const selectedCell = useMemo(() => {
    if (!selectedCellKey) return null;
    for (const row of matrix) {
      for (const cell of row) {
        if (`${cell.group}::${cell.module}` === selectedCellKey) return cell;
      }
    }
    return null;
  }, [matrix, selectedCellKey]);
  // ─── Loading state ────────────────────────────────────────────────────
  if (isRunning && allModules.length === 0) {
    return <div className="space-y-6" data-id="element-4884">
        <SkeletonHero data-id="element-4885" />
        <SkeletonMatrix data-id="element-4886" />
      </div>;
  }
  // ─── Empty state (no data, no run yet) ────────────────────────────────
  if (allModules.length === 0) {
    return <Card className="p-10 text-center bg-gradient-to-br from-white to-amber-50/30 border-amber-100" data-id="element-4887">
        <Layers className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-4888" />
        <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-4889">
          Nog geen toets-data beschikbaar
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-4890">
          De assessment-pool wordt geanalyseerd zodra de validatie-pipeline
          loopt. Klik op{' '}
          <span className="font-semibold text-gray-700" data-id="element-4891">Hervalideer</span> in de
          rechterbovenhoek om een verse run te starten.
        </p>
      </Card>;
  }
  return <div className="space-y-6" data-id="element-4892">
      {/* ── 1. HERO ── */}
      <HeroHeader counts={matrixCounts} data-id="element-4893" />

      {/* ── 2. KPI STRIP ── */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.05
    }} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4894">
        <KpiTile label="Klaar voor toets" value={matrixCounts.ok} suffix={`/ ${matrixCounts.total}`} color="emerald" icon={<CheckCircle2 className="w-4 h-4" data-id="element-4896" />} hint="Groep × module combinaties die een volledige 30-vragen toets aankunnen." data-id="element-4895" />
        <KpiTile label="Op het randje" value={matrixCounts.degraded + matrixCounts.shortfall} color="amber" icon={<Target className="w-4 h-4" data-id="element-4898" />} hint="Combinaties met tekort of onbalans — toets is nog op te leveren, maar minder representatief." data-id="element-4897" />
        <KpiTile label="Niet beschikbaar" value={matrixCounts.zero} color="red" icon={<XCircle className="w-4 h-4" data-id="element-4900" />} hint="Combinaties met 0 MC-vragen — kan op dit moment geen toets opleveren." data-id="element-4899" />
        <KpiTile label="Pool-bevindingen" value={report.affectedSlices} color="indigo" icon={<Layers className="w-4 h-4" data-id="element-4902" />} hint="Aantal (module, tier-band) slices met capaciteit- of spreidingsbevindingen." data-id="element-4901" />
      </motion.div>

      {/* ── 3. FEASIBILITY MATRIX ── */}
      <Card className="p-5" data-id="element-4903">
        <div className="flex items-start justify-between gap-4 mb-4" data-id="element-4904">
          <div className="min-w-0" data-id="element-4905">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4906">
              <Sparkles className="w-4 h-4 text-amber-500" data-id="element-4907" />
              Toets-feasibility per groep × module
            </h3>
            <p className="text-[11px] text-gray-500 mt-1 leading-relaxed" data-id="element-4908">
              Elke cel toont of een complete 30-vragen Cito-toets opgesteld kan
              worden voor die combinatie. Klik op een cel voor details.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 flex-shrink-0" data-id="element-4909">
            <LegendDot color="emerald" label="Klaar" data-id="element-4910" />
            <LegendDot color="amber" label="Onbalans" data-id="element-4911" />
            <LegendDot color="orange" label="Tekort" data-id="element-4912" />
            <LegendDot color="red" label="0 vragen" data-id="element-4913" />
            <LegendDot color="gray" label="—" data-id="element-4914" />
          </div>
        </div>

        <FeasibilityMatrix modules={allModules} matrix={matrix} selectedCellKey={selectedCellKey} onSelectCell={key => setSelectedCellKey(prev => prev === key ? null : key)} data-id="element-4915" />

        {/* Selected cell detail panel */}
        {selectedCell && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} transition={{
        duration: 0.2
      }} className="mt-4 overflow-hidden" data-id="element-4916">
            <CellDetailPanel cell={selectedCell} onSelectQuestion={onSelectQuestion} onClose={() => setSelectedCellKey(null)} data-id="element-4917" />
          </motion.div>}

        {/* Legend (mobile) */}
        <div className="flex sm:hidden flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100" data-id="element-4918">
          <LegendDot color="emerald" label="Klaar" data-id="element-4919" />
          <LegendDot color="amber" label="Onbalans" data-id="element-4920" />
          <LegendDot color="orange" label="Tekort" data-id="element-4921" />
          <LegendDot color="red" label="0 vragen" data-id="element-4922" />
          <LegendDot color="gray" label="—" data-id="element-4923" />
        </div>
      </Card>

      {/* ── 4. FILTER STRIP ── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-4924">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 px-1" data-id="element-4925">
          <FilterIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-4926" />
          Filter
        </div>
        <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle modules'
      }, ...allModules.map(m => ({
        value: m,
        label: m
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4927" />
        <Select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle groepen'
      }, {
        value: '6',
        label: 'Groep 6'
      }, {
        value: '7',
        label: 'Groep 7'
      }, {
        value: '8',
        label: 'Groep 8'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4928" />
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'zero_questions',
        label: '0 vragen'
      }, {
        value: 'shortfall',
        label: 'Tekort'
      }, {
        value: 'degraded',
        label: 'Onbalans'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4929" />
        <div className="flex-1" data-id="element-4930" />
        <span className="text-[11px] text-gray-500 tabular-nums px-1" data-id="element-4931">
          {filteredFeasibility.length} feasibility · {filteredSlices.length}{' '}
          pool-slices
        </span>
      </div>

      {/* ── 5. TWO-COLUMN DETAIL ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4932">
        {/* Feasibility findings list */}
        <Card className="p-5" data-id="element-4933">
          <div className="flex items-center justify-between mb-3" data-id="element-4934">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4935">
              <ClipboardList className="w-4 h-4 text-orange-500" data-id="element-4936" />
              Wat ontbreekt voor een toets?
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-4937">
              Layer C
            </span>
          </div>
          {filteredFeasibility.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-4939" />} text="Geen feasibility-bevindingen op deze filter." data-id="element-4938" /> : <div className="space-y-1.5" data-id="element-4940">
              {filteredFeasibility.map((finding, idx) => <FeasibilityRow key={finding.comboKey} finding={finding} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-4941" />)}
            </div>}
        </Card>

        {/* Capacity + Distribution slice findings */}
        <Card className="p-5" data-id="element-4942">
          <div className="flex items-center justify-between mb-3" data-id="element-4943">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4944">
              <ListChecks className="w-4 h-4 text-rose-500" data-id="element-4945" />
              Pool-capaciteit & spreiding
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-4946">
              Layer A · B
            </span>
          </div>
          {/* Layer A/B stat tiles */}
          <div className="grid grid-cols-4 gap-1.5 mb-3" data-id="element-4947">
            <MiniStat label="Gat" count={report.capacityCounts.gap} tone="red" data-id="element-4948" />
            <MiniStat label="Dun" count={report.capacityCounts.thin} tone="amber" data-id="element-4949" />
            <MiniStat label="Cito-scheef" count={report.distributionCounts.citoConcentration} tone="orange" data-id="element-4950" />
            <MiniStat label="DOK-smal" count={report.distributionCounts.dokCoverage} tone="rose" data-id="element-4951" />
          </div>
          {filteredSlices.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-4953" />} text="Geen pool-bevindingen op deze filter." data-id="element-4952" /> : <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1" data-id="element-4954">
              {filteredSlices.map((slice, idx) => <SliceRow key={slice.sliceKey} slice={slice} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-4955" />)}
            </div>}
        </Card>
      </div>

      {/* ── 6. DECISION HINT FOOTER ── */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100" data-id="element-4956">
        <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-4957" />
        <p className="text-xs text-amber-900 leading-relaxed" data-id="element-4958">
          <span className="font-semibold" data-id="element-4959">Aanpakvolgorde:</span> begin met{' '}
          <span className="font-semibold" data-id="element-4960">0 vragen</span> (rode cellen) en{' '}
          <span className="font-semibold" data-id="element-4961">pool-gat</span> — die kunnen nu al
          geen toets opleveren. <em data-id="element-4962">Tekort</em> en <em data-id="element-4963">dun</em> werken op het
          randje. <em data-id="element-4964">Onbalans</em> en <em data-id="element-4965">cito-scheef</em> blijven functioneel
          maar minder representatief voor de hele curriculumstrook. Alle
          bevindingen zijn diagnostisch (coach/info) — nooit blocker.
        </p>
      </div>
    </div>;
}
// ─── Hero Header ──────────────────────────────────────────────────────────
function HeroHeader({
  counts
}: {
  counts: {
    ok: number;
    degraded: number;
    shortfall: number;
    zero: number;
    unknown: number;
    total: number;
    readinessPct: number;
  };
}) {
  const allReady = counts.ok === counts.total && counts.total > 0;
  const ringColor = allReady ? 'text-emerald-500' : counts.zero > 0 ? 'text-red-500' : 'text-amber-500';
  const ringBg = allReady ? 'from-emerald-50 to-white' : counts.zero > 0 ? 'from-red-50 to-white' : 'from-amber-50 to-white';
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br ${ringBg} p-6`} data-id="element-4966">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-4967">
        {/* Readiness ring */}
        <div className="relative flex-shrink-0" data-id="element-4968">
          <svg width="96" height="96" viewBox="0 0 96 96" className="rotate-[-90deg]" data-id="element-4969">
            <circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200" data-id="element-4970" />
            <motion.circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" className={ringColor} initial={{
            pathLength: 0
          }} animate={{
            pathLength: counts.readinessPct / 100
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} style={{
            strokeDasharray: 2 * Math.PI * 42
          }} data-id="element-4971" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-4972">
            <span className="text-2xl font-bold text-gray-900 tabular-nums leading-none" data-id="element-4973">
              {counts.readinessPct}%
            </span>
            <span className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-4974">
              klaar
            </span>
          </div>
        </div>

        {/* Header text */}
        <div className="flex-1 min-w-0" data-id="element-4975">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-4976">
            <Layers className="w-4 h-4 text-amber-500" data-id="element-4977" />
            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider" data-id="element-4978">
              Assessment Pool · Stage 6b
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-4979">
            Toets-feasibility
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed max-w-2xl" data-id="element-4980">
            {allReady ? <>
                <span className="font-semibold text-emerald-700" data-id="element-4981">
                  Alle {counts.total} groep × module combinaties
                </span>{' '}
                kunnen een volledige Cito-style toets opleveren. Pool-capaciteit
                en spreiding zijn gezond.
              </> : <>
                <span className="font-semibold text-gray-900" data-id="element-4982">{counts.ok}</span>{' '}
                van{' '}
                <span className="font-semibold text-gray-900" data-id="element-4983">
                  {counts.total}
                </span>{' '}
                combinaties kan nu een volledige toets opleveren.{' '}
                {counts.zero > 0 && <>
                    <span className="text-red-700 font-semibold" data-id="element-4984">
                      {counts.zero}
                    </span>{' '}
                    {counts.zero === 1 ? 'combinatie' : 'combinaties'} heeft
                    geen MC-vragen.{' '}
                  </>}
                {counts.degraded + counts.shortfall > 0 && <>
                    <span className="text-amber-700 font-semibold" data-id="element-4985">
                      {counts.degraded + counts.shortfall}
                    </span>{' '}
                    {counts.degraded + counts.shortfall === 1 ? 'staat' : 'staan'}{' '}
                    op het randje.
                  </>}
              </>}
          </p>
        </div>
      </div>
    </motion.div>;
}
// ─── KPI Tile ─────────────────────────────────────────────────────────────
function KpiTile({
  label,
  value,
  suffix,
  color,
  icon,
  hint
}: {
  label: string;
  value: number;
  suffix?: string;
  color: 'emerald' | 'amber' | 'red' | 'indigo';
  icon: React.ReactNode;
  hint: string;
}) {
  const palette = {
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-700',
      iconBg: 'bg-emerald-100'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-700',
      iconBg: 'bg-amber-100'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-700',
      iconBg: 'bg-red-100'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
      text: 'text-indigo-700',
      iconBg: 'bg-indigo-100'
    }
  }[color];
  return <div className={`relative ${palette.bg} ${palette.border} border rounded-xl p-3 group`} title={hint} data-id="element-4986">
      <div className="flex items-start justify-between mb-2" data-id="element-4987">
        <div className={`w-7 h-7 rounded-lg ${palette.iconBg} ${palette.text} flex items-center justify-center`} data-id="element-4988">
          {icon}
        </div>
      </div>
      <p className={`text-2xl font-bold ${palette.text} tabular-nums leading-none`} data-id="element-4989">
        {value}
        {suffix && <span className="text-sm font-medium ml-1 opacity-70" data-id="element-4990">{suffix}</span>}
      </p>
      <p className={`text-xs font-medium ${palette.text} mt-1.5`} data-id="element-4991">{label}</p>
    </div>;
}
// ─── Feasibility Matrix ──────────────────────────────────────────────────
function FeasibilityMatrix({
  modules,
  matrix,
  selectedCellKey,
  onSelectCell
}: {
  modules: string[];
  matrix: MatrixCell[][];
  selectedCellKey: string | null;
  onSelectCell: (key: string) => void;
}) {
  return <div className="overflow-x-auto -mx-2 px-2" data-id="element-4992">
      <table className="min-w-full border-separate border-spacing-1" data-id="element-4993">
        <thead data-id="element-4994">
          <tr data-id="element-4995">
            <th className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left pl-2 pb-1 w-16" data-id="element-4996">
              Groep
            </th>
            {modules.map(module => {
            const cfg = MODULE_CONFIG[module];
            const bg = cfg?.bgClass ?? 'bg-gray-400';
            return <th key={module} className="text-[10px] font-bold text-gray-600 text-center pb-1 px-0.5 min-w-[78px]" data-id="element-4997">
                  <div className="flex flex-col items-center gap-1" data-id="element-4998">
                    <div className={`w-1.5 h-1.5 rounded-full ${bg}`} data-id="element-4999" />
                    <span className="font-mono" data-id="element-5000">{module}</span>
                  </div>
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-5001">
          {matrix.map((row, rowIdx) => {
          const group = row[0]?.group;
          return <tr key={group ?? rowIdx} data-id="element-5002">
                <td className="text-xs font-semibold text-gray-700 pl-2 pr-1 align-middle" data-id="element-5003">
                  Groep {group}
                </td>
                {row.map((cell, idx) => {
              const key = `${cell.group}::${cell.module}`;
              const selected = key === selectedCellKey;
              return <td key={key} className="p-0" data-id="element-5004">
                      <motion.button type="button" onClick={() => onSelectCell(key)} initial={{
                  opacity: 0,
                  scale: 0.92
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.2,
                  delay: 0.02 * (rowIdx * modules.length + idx)
                }} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.97
                }} className={`w-full ${getCellClass(cell.status, selected)}`} aria-label={`${cell.module} groep ${cell.group} — ${getStatusLabel(cell.status)}`} data-id="element-5005">
                        <CellContent cell={cell} data-id="element-5006" />
                      </motion.button>
                    </td>;
            })}
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
function getCellClass(status: StatusKind, selected: boolean): string {
  const ring = selected ? 'ring-2 ring-offset-1 ring-gray-900' : '';
  if (status === 'ok') return `${ring} h-14 rounded-lg bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 transition-all flex items-center justify-center`;
  if (status === 'degraded') return `${ring} h-14 rounded-lg bg-amber-50 border border-amber-200 hover:border-amber-400 hover:bg-amber-100 transition-all flex items-center justify-center`;
  if (status === 'shortfall') return `${ring} h-14 rounded-lg bg-orange-50 border border-orange-200 hover:border-orange-400 hover:bg-orange-100 transition-all flex items-center justify-center`;
  if (status === 'zero') return `${ring} h-14 rounded-lg bg-red-50 border border-red-200 hover:border-red-400 hover:bg-red-100 transition-all flex items-center justify-center`;
  return `${ring} h-14 rounded-lg bg-gray-50 border border-gray-200 hover:border-gray-300 transition-all flex items-center justify-center`;
}
function getStatusLabel(status: StatusKind): string {
  if (status === 'ok') return 'klaar';
  if (status === 'degraded') return 'onbalans';
  if (status === 'shortfall') return 'tekort';
  if (status === 'zero') return '0 vragen';
  return 'geen data';
}
function CellContent({
  cell
}: {
  cell: MatrixCell;
}) {
  if (cell.status === 'ok') {
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-5007" />;
  }
  if (cell.status === 'unknown') {
    return <span className="text-gray-400 text-xs" data-id="element-5008">—</span>;
  }
  const f = cell.finding;
  if (!f) return null;
  if (f.reason === 'zero_questions') {
    return <div className="flex flex-col items-center leading-none" data-id="element-5009">
        <XCircle className="w-3.5 h-3.5 text-red-600 mb-0.5" data-id="element-5010" />
        <span className="text-[10px] font-bold text-red-700" data-id="element-5011">0</span>
      </div>;
  }
  if (typeof f.achievable === 'number' && typeof f.target === 'number') {
    const color = f.reason === 'shortfall' ? 'text-orange-700' : 'text-amber-700';
    return <div className="flex flex-col items-center leading-tight" data-id="element-5012">
        <span className={`text-[11px] font-bold ${color} tabular-nums`} data-id="element-5013">
          {f.achievable}/{f.target}
        </span>
        <span className="text-[9px] text-gray-500 mt-0.5" data-id="element-5014">
          {f.reason === 'shortfall' ? 'tekort' : 'onbalans'}
        </span>
      </div>;
  }
  return <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-5015" />;
}
// ─── Cell Detail Panel ───────────────────────────────────────────────────
function CellDetailPanel({
  cell,
  onSelectQuestion,
  onClose
}: {
  cell: MatrixCell;
  onSelectQuestion: (questionId: string) => void;
  onClose: () => void;
}) {
  const cfg = MODULE_CONFIG[cell.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const f = cell.finding;
  return <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-5016">
      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-5017">
        <div className="flex items-center gap-3 min-w-0" data-id="element-5018">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${bg}`} data-id="element-5019" />
          <div data-id="element-5020">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-5021">
              Groep {cell.group} ·{' '}
              <span className="font-mono" data-id="element-5022">{cell.module}</span>
            </h4>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5023">
              {getStatusLabel(cell.status)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded transition-colors" data-id="element-5024">
          Sluiten
        </button>
      </div>

      {cell.status === 'ok' ? <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100" data-id="element-5025">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-5026" />
          <p className="text-xs text-emerald-800" data-id="element-5027">
            Deze combinatie kan een volledige 30-vragen Cito-style toets
            opleveren met de gewenste tier-band verhouding (35% laag · 40%
            midden · 25% hoog).
          </p>
        </div> : cell.status === 'unknown' ? <p className="text-xs text-gray-500" data-id="element-5028">
          Geen pool-data beschikbaar voor deze combinatie in de huidige run.
        </p> : f ? <div className="space-y-3" data-id="element-5029">
          {/* Target vs achievable bars */}
          {typeof f.target === 'number' && <div data-id="element-5030">
              <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-5031">
                <span className="text-gray-600 font-medium" data-id="element-5032">
                  Bereikbaar / Doel
                </span>
                <span className="text-gray-900 font-semibold tabular-nums" data-id="element-5033">
                  {f.achievable ?? 0} / {f.target}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5034">
                <div className={`h-full rounded-full ${f.reason === 'zero_questions' ? 'bg-red-500' : f.reason === 'shortfall' ? 'bg-orange-500' : 'bg-amber-500'}`} style={{
            width: `${Math.min(100, (f.achievable ?? 0) / f.target * 100)}%`
          }} data-id="element-5035" />
              </div>
            </div>}

          {/* Per-band breakdown */}
          {f.bandAchievable && f.bandTarget && <div className="grid grid-cols-3 gap-2" data-id="element-5036">
              <BandTile label="Laag" achievable={f.bandAchievable.low} target={f.bandTarget.low} isEmpty={f.emptyBands?.includes('low')} data-id="element-5037" />
              <BandTile label="Midden" achievable={f.bandAchievable.mid} target={f.bandTarget.mid} isEmpty={f.emptyBands?.includes('mid')} data-id="element-5038" />
              <BandTile label="Hoog" achievable={f.bandAchievable.high} target={f.bandTarget.high} isEmpty={f.emptyBands?.includes('high')} data-id="element-5039" />
            </div>}

          {/* CTA */}
          {f.issue.questionId && f.issue.questionId !== 'BATCH' && f.issue.questionId !== 'UNKNOWN' && <button onClick={() => onSelectQuestion(f.issue.questionId)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 hover:bg-amber-50 hover:border-amber-200 transition-colors group" data-id="element-5040">
                <span className="text-xs text-gray-700" data-id="element-5041">
                  Bekijk representatieve vraag in{' '}
                  <span className="font-semibold" data-id="element-5042">Vragen</span>
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" data-id="element-5043" />
              </button>}
        </div> : null}
    </div>;
}
function BandTile({
  label,
  achievable,
  target,
  isEmpty
}: {
  label: string;
  achievable: number;
  target: number;
  isEmpty?: boolean;
}) {
  const ratio = target > 0 ? achievable / target : 0;
  const status = isEmpty || achievable === 0 ? 'red' : ratio < 1 ? 'amber' : 'emerald';
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700'
  }[status];
  return <div className={`${palette} border rounded-lg p-2 text-center`} data-id="element-5044">
      <p className="text-[9px] font-bold uppercase tracking-wider opacity-80" data-id="element-5045">
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums mt-0.5" data-id="element-5046">
        {achievable}
        <span className="text-[10px] opacity-60" data-id="element-5047">/{target}</span>
      </p>
    </div>;
}
// ─── Row components ──────────────────────────────────────────────────────
function FeasibilityRow({
  finding,
  rank,
  onSelectQuestion
}: {
  finding: AssessmentPoolFeasibilityFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[finding.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const questionId = finding.issue.questionId;
  const hasQuestion = questionId && questionId !== 'BATCH' && questionId !== 'UNKNOWN';
  const tone: 'red' | 'orange' | 'amber' = finding.reason === 'zero_questions' ? 'red' : finding.reason === 'shortfall' ? 'orange' : 'amber';
  const tagLabel = finding.reason === 'zero_questions' ? '0 vragen' : finding.reason === 'shortfall' ? 'tekort' : 'onbalans';
  const toneClasses: Record<'red' | 'orange' | 'amber', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100'
  };
  return <button type="button" disabled={!hasQuestion} onClick={() => hasQuestion && onSelectQuestion(questionId)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${hasQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5048">
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5049">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5050" />
      <div className="min-w-0 flex-1" data-id="element-5051">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5052">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5053">
            Groep {finding.group}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5054">
            / {finding.module}
          </span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[tone]}`} data-id="element-5055">
            {tagLabel}
          </span>
        </div>
        {typeof finding.achievable === 'number' && typeof finding.target === 'number' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5056">
            {finding.achievable}/{finding.target} bereikbaar
            {finding.emptyBands && finding.emptyBands.length > 0 && <>
                {' '}
                · lege banden:{' '}
                <span className="font-mono text-amber-700" data-id="element-5057">
                  {finding.emptyBands.join(', ')}
                </span>
              </>}
          </p> : finding.reason === 'zero_questions' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5058">
            0 MC-vragen ·{' '}
            <span className="text-red-600 font-semibold" data-id="element-5059">
              +{finding.target ?? 30} nodig
            </span>
          </p> : null}
      </div>
      {hasQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5060" />}
    </button>;
}
function SliceRow({
  slice,
  rank,
  onSelectQuestion
}: {
  slice: AssessmentPoolSliceFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[slice.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const representativeQuestion = slice.issues.find(i => i.questionId)?.questionId;
  const tags: Array<{
    label: string;
    tone: 'red' | 'amber' | 'orange' | 'rose';
  }> = [];
  if (slice.capacityStatus === 'gap') tags.push({
    label: 'gat',
    tone: 'red'
  });else if (slice.capacityStatus === 'thin') tags.push({
    label: 'dun',
    tone: 'amber'
  });
  if (slice.distributionReasons.includes('cito_concentration')) tags.push({
    label: 'cito-scheef',
    tone: 'orange'
  });
  if (slice.distributionReasons.includes('dok_coverage')) tags.push({
    label: 'dok-smal',
    tone: 'rose'
  });
  const toneClasses: Record<'red' | 'amber' | 'orange' | 'rose', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    rose: 'text-rose-700 bg-rose-50 border-rose-100'
  };
  return <button type="button" disabled={!representativeQuestion} onClick={() => representativeQuestion && onSelectQuestion(representativeQuestion)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${representativeQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5061">
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5062">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5063" />
      <div className="min-w-0 flex-1" data-id="element-5064">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5065">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5066">
            {slice.module}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5067">
            / {slice.tierBand}
          </span>
          {tags.map(t => <span key={t.label} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[t.tone]}`} data-id="element-5068">
              {t.label}
            </span>)}
        </div>
        {typeof slice.mcEligibleCount === 'number' && typeof slice.minimumViableSize === 'number' && <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5069">
              {slice.mcEligibleCount} MC-vragen · minimum{' '}
              {slice.minimumViableSize}
              {typeof slice.remediationNeeded === 'number' && slice.remediationNeeded > 0 && <>
                    {' '}
                    ·{' '}
                    <span className="text-red-600 font-semibold" data-id="element-5070">
                      +{slice.remediationNeeded} nodig
                    </span>
                  </>}
            </p>}
      </div>
      {representativeQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5071" />}
    </button>;
}
// ─── Small helpers ───────────────────────────────────────────────────────
function MiniStat({
  label,
  count,
  tone
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber' | 'orange' | 'rose';
}) {
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    orange: 'bg-orange-50 border-orange-100 text-orange-700',
    rose: 'bg-rose-50 border-rose-100 text-rose-700'
  }[tone];
  return <div className={`${palette} border rounded-lg px-1.5 py-1.5 text-center`} data-id="element-5072">
      <p className="text-base font-bold tabular-nums leading-none" data-id="element-5073">{count}</p>
      <p className="text-[9px] font-medium mt-0.5 truncate" data-id="element-5074">{label}</p>
    </div>;
}
function LegendDot({
  color,
  label
}: {
  color: 'emerald' | 'amber' | 'orange' | 'red' | 'gray';
  label: string;
}) {
  const cls = {
    emerald: 'bg-emerald-400',
    amber: 'bg-amber-400',
    orange: 'bg-orange-400',
    red: 'bg-red-500',
    gray: 'bg-gray-300'
  }[color];
  return <div className="flex items-center gap-1.5" data-id="element-5075">
      <div className={`w-2 h-2 rounded-full ${cls}`} data-id="element-5076" />
      <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-5077">
        {label}
      </span>
    </div>;
}
function EmptyTile({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-100" data-id="element-5078">
      <div className="mx-auto mb-2 w-fit" data-id="element-5079">{icon}</div>
      <p className="text-xs text-gray-600" data-id="element-5080">{text}</p>
    </div>;
}
// ─── Skeleton states ─────────────────────────────────────────────────────
function SkeletonHero() {
  return <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 animate-pulse" data-id="element-5081">
      <div className="flex items-center gap-6" data-id="element-5082">
        <div className="w-24 h-24 rounded-full bg-gray-200" data-id="element-5083" />
        <div className="flex-1 space-y-2" data-id="element-5084">
          <div className="h-3 w-32 bg-gray-200 rounded" data-id="element-5085" />
          <div className="h-5 w-48 bg-gray-200 rounded" data-id="element-5086" />
          <div className="h-3 w-full bg-gray-200 rounded" data-id="element-5087" />
          <div className="h-3 w-3/4 bg-gray-200 rounded" data-id="element-5088" />
        </div>
      </div>
    </div>;
}
function SkeletonMatrix() {
  return <div className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse" data-id="element-5089">
      <div className="h-4 w-48 bg-gray-200 rounded mb-4" data-id="element-5090" />
      <div className="grid grid-cols-8 gap-1.5" data-id="element-5091">
        {Array.from({
        length: 24
      }).map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" data-id="element-5092" />)}
      </div>
    </div>;
}